import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Search, Loader2, CheckCircle2, Clock, XCircle, Calendar, Building2, Layers, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";

const ALL = "__all__";
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("es-ES") : "—");
const money = (n, cur = "EUR") => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur === "USD" ? "$" : "€"}`;

const ESTADOS = {
  no_solicitada: { label: "No solicitada", icon: XCircle, cls: "border-zinc-200 bg-zinc-50 text-zinc-600", tile: "bg-zinc-100 text-zinc-700" },
  solicitada: { label: "Solicitada · sin recibir", icon: Clock, cls: "border-amber-200 bg-amber-50 text-amber-700", tile: "bg-amber-100 text-amber-700" },
  recibida: { label: "Recibida", icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-700", tile: "bg-emerald-100 text-emerald-700" },
  mensual: { label: "Mensual", icon: Calendar, cls: "border-indigo-200 bg-indigo-50 text-indigo-700", tile: "bg-indigo-100 text-indigo-700" },
  no_aplica: { label: "No aplica", icon: Layers, cls: "border-zinc-200 bg-zinc-50 text-zinc-400", tile: "bg-zinc-100 text-zinc-500" },
};
const ORDER = ["no_solicitada", "solicitada", "recibida", "mensual", "no_aplica"];

function DateDialog({ open, onClose, row, onConfirm, pending }) {
  const [fecha, setFecha] = useState(todayISO());
  React.useEffect(() => { if (open) setFecha(todayISO()); }, [open]);
  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pe-date-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Registrar solicitud de factura</DialogTitle>
          <DialogDescription className="text-xs">{row.service} · {row.provider_name || "—"}. Se guarda la fecha y quién la solicita.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label className="text-xs">Fecha de solicitud *</Label>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="pe-date-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onConfirm(fecha)} disabled={!fecha || pending} data-testid="pe-date-confirm">
            {pending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />}Marcar solicitada
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PorEstadoTab() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState(ALL);
  const [providerId, setProviderId] = useState(ALL);
  const [agent, setAgent] = useState(ALL);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [dateRow, setDateRow] = useState(null);

  const params = useMemo(() => ({
    estado: estado === ALL ? undefined : estado,
    provider_id: providerId === ALL ? undefined : providerId,
    agent: agent === ALL ? undefined : agent,
    date_from: from || undefined,
    date_to: to || undefined,
    q: q.trim() || undefined,
  }), [estado, providerId, agent, from, to, q]);

  const listQ = useQuery({ queryKey: ["por-estado", params], queryFn: async () => (await api.get("/solicitud-factura/por-estado", { params })).data });
  const rows = listQ.data?.reservas || [];
  const summary = listQ.data?.summary || {};
  const total = listQ.data?.total || { count: 0, cost: 0 };
  const providers = listQ.data?.providers || [];
  const agents = listQ.data?.agents || [];

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["por-estado"] }); qc.invalidateQueries({ queryKey: ["sf-list"] }); qc.invalidateQueries({ queryKey: ["sf-trips"] }); };
  const solicitarMut = useMutation({
    mutationFn: async ({ rid, fecha }) => (await api.post(`/solicitud-factura/reserva/${rid}/solicitar`, { fecha })).data,
    onSuccess: () => { toast.success("Factura solicitada"); invalidate(); setDateRow(null); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const recibidaMut = useMutation({
    mutationFn: async (rid) => (await api.post(`/solicitud-factura/reserva/${rid}/recibida`, { recibida: true })).data,
    onSuccess: () => { toast.success("Marcada como recibida"); invalidate(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div className="space-y-4" data-testid="por-estado-tab">
      {/* KPI tiles por estado */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {ORDER.map((k) => {
          const s = summary[k] || { count: 0, cost: 0 };
          const meta = ESTADOS[k];
          const Icon = meta.icon;
          const active = estado === k;
          return (
            <button key={k} type="button" onClick={() => setEstado(active ? ALL : k)}
              className={`flex items-center gap-3 rounded-xl border bg-card px-4 py-3 text-left transition-all hover:shadow-md ${active ? "border-primary ring-2 ring-primary/30" : ""}`}
              data-testid={`pe-tile-${k}`}>
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.tile}`}><Icon className="h-4 w-4" /></div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{meta.label}</p>
                <p className="text-lg font-bold leading-none tabular-nums">{s.count}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{money(s.cost)}</p>
              </div>
            </button>
          );
        })}
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="space-y-1">
            <Label className="text-xs">Estado</Label>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="h-9 w-[200px]" data-testid="pe-filter-estado"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos los estados</SelectItem>
                {ORDER.map((k) => <SelectItem key={k} value={k}>{ESTADOS[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Proveedor</Label>
            <Select value={providerId} onValueChange={setProviderId}>
              <SelectTrigger className="h-9 w-[210px]" data-testid="pe-filter-provider"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value={ALL}>Todos</SelectItem>
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Agente</Label>
            <Select value={agent} onValueChange={setAgent}>
              <SelectTrigger className="h-9 w-[170px]" data-testid="pe-filter-agent"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos</SelectItem>
                {agents.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Desde</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" data-testid="pe-date-from" /></div>
          <div className="space-y-1"><Label className="text-xs">Hasta</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" data-testid="pe-date-to" /></div>
          <div className="relative min-w-[180px] flex-1 space-y-1">
            <Label className="text-xs">Buscar</Label>
            <Search className="absolute left-2.5 top-[30px] h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cliente, servicio, proveedor…" className="h-9 pl-8" data-testid="pe-search" />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="pe-summary">
        <Badge variant="secondary">{rows.length} reserva(s) mostradas</Badge>
        <span className="text-xs text-muted-foreground">· {total.count} en total · coste {money(total.cost)}</span>
      </div>

      {listQ.isLoading ? <Skeleton className="h-64 w-full" /> : rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="pe-empty">No hay reservas con los filtros aplicados.</CardContent></Card>
      ) : (
        <div className="max-h-[62vh] overflow-auto rounded-xl border">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-zinc-50 text-[10px] uppercase tracking-wide text-muted-foreground shadow-sm">
              <tr>
                <th className="px-3 py-3">Cliente</th>
                <th className="px-3 py-3">Servicio</th>
                <th className="px-3 py-3">Proveedor</th>
                <th className="px-3 py-3 text-right">Coste</th>
                <th className="px-3 py-3">Estado</th>
                <th className="px-3 py-3">Solicitada</th>
                <th className="px-3 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = ESTADOS[r.estado_fact] || ESTADOS.no_solicitada;
                const Icon = meta.icon;
                return (
                  <tr key={r.id} className="border-t align-middle hover:bg-zinc-50/50" data-testid={`pe-row-${r.id}`}>
                    <td className="px-3 py-2.5"><div className="font-medium">{r.client_name}</div><div className="text-[11px] text-muted-foreground">{r.agent}</div></td>
                    <td className="px-3 py-2.5"><div className="font-medium">{r.service}</div><div className="text-[11px] text-muted-foreground">{[fmtD(r.date_entry), r.city].filter(Boolean).join(" · ")}</div></td>
                    <td className="px-3 py-2.5"><div className="flex items-center gap-1.5"><Building2 className="h-3 w-3 text-muted-foreground" />{r.provider_name || "—"}</div></td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{r.cost ? money(r.cost, r.currency) : "—"}</td>
                    <td className="px-3 py-2.5"><Badge variant="outline" className={`gap-1 text-[10px] ${meta.cls}`} data-testid={`pe-estado-${r.id}`}><Icon className="h-3 w-3" />{meta.label}</Badge></td>
                    <td className="px-3 py-2.5 text-[11px] text-muted-foreground">{r.solicitada ? <>{fmtD(r.solicitada_at)}<br />{r.solicitada_by}</> : "—"}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        {r.estado_fact === "no_solicitada" && (
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setDateRow(r)} data-testid={`pe-solicitar-${r.id}`}><FileText className="mr-1 h-3.5 w-3.5" />Solicitar</Button>
                        )}
                        {r.estado_fact === "solicitada" && (
                          <Button size="sm" variant="outline" className="h-8 text-emerald-700" onClick={() => recibidaMut.mutate(r.id)} data-testid={`pe-recibida-${r.id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Recibida</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DateDialog open={!!dateRow} onClose={() => setDateRow(null)} row={dateRow}
        pending={solicitarMut.isPending}
        onConfirm={(fecha) => solicitarMut.mutate({ rid: dateRow.id, fecha })} />
    </div>
  );
}
